// SceneryMaterials — Set15 P7 (§3.8): the shared bark/leaf MeshStandardMaterial builder, promoted
// out of `ImpostorBaker` (Set14 P4 §4.5 created it inline there). The bake uses it to PRE-LIT the
// impostor atlas; the 3D near tier uses the SAME builder for its real InstancedMeshes, so an
// impostor and a real mesh differ only in RESOLUTION, never in shading model (Set14 goal §1.2).
//
// The bake's material is `MeshStandardMaterial` + real lights (bark opaque FrontSide, leaf
// alpha-tested DoubleSide). The near tier renders with these same materials under the SAME light
// rig (a `Map3D_SceneLights` directional+ambient pair derived from the terrain shading config),
// which is what keeps the impostor/near-tier crossfade from visibly flattening.
//
// This module is renderer-side (it builds `THREE.MeshStandardMaterial` and reads the texture
// cache). It is intentionally imported by the RENDERER + the BAKE, NOT by the worker (which must
// stay DOM-free) — the worker only generates geometry (`SceneryMesh`); materials are always built
// renderer-side.
import * as THREE from "three";
import {type ParamValue, type SceneryMeshPart} from "../@SceneryTypes.js";
import {getBarkTextures, getLeafTexture} from "./SceneryTextures.js";

// ── Param readers (a variant's flat resolved params) ─────────────────────────────────────────
/** Read a numeric flat param, falling back when absent/NaN (never a bare `>`/`<` against a
 *  possibly-absent field — resolve through a default first, Set13 §8). */
export function pnum(params: Record<string, ParamValue>, key: string, fallback: number): number {
	const v = params[key];
	return (typeof v == "number" && Number.isFinite(v)) ? v : fallback;
}
/** Read a string flat param, falling back when absent. */
export function pstr(params: Record<string, ParamValue>, key: string, fallback: string): string {
	const v = params[key];
	return typeof v == "string" ? v : fallback;
}
/** Read a boolean flat param, falling back when absent. */
export function pbool(params: Record<string, ParamValue>, key: string, fallback: boolean): boolean {
	const v = params[key];
	return typeof v == "boolean" ? v : fallback;
}

/** Build a three.js BufferGeometry from ONE `SceneryMeshPart`. Indices are uint32 (§2.3 — the
 *  worker emits Uint32Array; a foliage-heavy ez-tree leaf part already measures ~21 760 verts).
 *  Set14 P4 §4.5 — the transitional per-vertex `colors` are GONE (removed from the contract);
 *  the tint comes from the MeshStandardMaterial, so no `color` attribute is set. */
export function partToGeometry(part: SceneryMeshPart): THREE.BufferGeometry {
	const geo = new THREE.BufferGeometry();
	geo.setAttribute("position", new THREE.BufferAttribute(part.positions, 3));
	geo.setAttribute("normal", new THREE.BufferAttribute(part.normals, 3));
	if (part.uvs.length > 0) geo.setAttribute("uv", new THREE.BufferAttribute(part.uvs, 2));
	geo.setIndex(new THREE.BufferAttribute(part.indices, 1));
	return geo;
}

/** The material pair for one variant's resolved params. `bark` = opaque FrontSide trunk/branches;
 *  `leaf` = alpha-tested DoubleSide canopy. Both are `MeshStandardMaterial` so the bake (pre-lit
 *  atlas) and the 3D near tier (real mesh under scene lights) render the SAME shading model. */
export interface SceneryMaterials {
	bark: THREE.MeshStandardMaterial;
	leaf: THREE.MeshStandardMaterial;
}

/** Build the bark + leaf MeshStandardMaterials for ONE variant's params. Mirrors ez-tree's
 *  `#createBarkMaterial` / `#createLeafMaterial` (§4.5): the SAME material the 3D near tier uses,
 *  so an impostor and a real mesh differ only in resolution (goal §1.2).
 *  The texture maps come from the SceneryTextures cache (renderer-side, §4.9); when a set isn't
 *  ready (or failed to load) the slot is null and the material falls back to its tint — degrade,
 *  never throw. Colour maps are sRGB; normal/roughness are linear (the §4.9/§8 convention).
 *  Wind sway (ez-tree's `onBeforeCompile` simplex noise) is OMITTED — `uTime = 0` freezes it, so
 *  a bake without it is byte-identical to a bake with it (§4.5: neutralised, not removed).
 *  Set15 P7 — the materials are now built ONCE per variant and SHARED by the bake AND the near
 *  tier's InstancedMesh(es) of that variant. The near tier sets per-instance tint via
 *  `instanceColor` (rgb = tint³), leaving the per-variant `material.color` = the authored tint. */
export function buildSceneryMaterials(params: Record<string, ParamValue>): SceneryMaterials {
	const bark = new THREE.MeshStandardMaterial({
		name: "branches",
		flatShading: pbool(params, "barkFlatShading", false),
		color: new THREE.Color(pnum(params, "barkTint", 0xffffff)),
		metalness: 0.0,
		roughness: 1.0,
	});
	if (pbool(params, "barkTextured", true)) {
		// textureScale.x is baked into UVs during meshing (wrapsX), so only the Y axis needs
		// runtime scaling on the texture itself (ez-tree's `#createBarkMaterial` convention).
		const scaleY = pnum(params, "barkTextureScaleY", 1);
		const maps = getBarkTextures(pstr(params, "barkType", "Bark001"));
		const apply = (tex: THREE.Texture | null): THREE.Texture | null => {
			if (!tex) return null;
			tex.wrapS = THREE.RepeatWrapping;
			tex.wrapT = THREE.RepeatWrapping;
			tex.repeat.x = 1;
			tex.repeat.y = 1 / scaleY;
			return tex;
		};
		if (maps?.color) bark.map = apply(maps.color);
		if (maps?.normal) bark.normalMap = apply(maps.normal);
		if (maps?.roughness) {
			bark.roughnessMap = apply(maps.roughness);
			// metalness stays 0 (factor 0); pointing metalnessMap at the same texture avoids
			// synthesising a merged metal/roughness PNG (ez-tree's GLTF-export rationale).
			bark.metalnessMap = bark.roughnessMap;
		}
	}

	const leaf = new THREE.MeshStandardMaterial({
		name: "leaves",
		map: getLeafTexture(pstr(params, "leafType", "oak")),
		color: new THREE.Color(0xffffff),
		side: THREE.DoubleSide,
		alphaTest: pnum(params, "leafAlphaTest", 0.5),
		metalness: 0.0,
		roughness: 1.0,
		dithering: true,
	});
	// `roundedNormals` is GEOMETRY (computed in ez-tree's `#meshLeaf`) and arrives in the normals
	// array; this override skips three's backface normal-flip when they're in use, so the canopy
	// normals aren't inverted on the leaf sprites' back faces (ez-tree's `#createLeafMaterial`).
	const roundedNormals = pbool(params, "leafRoundedNormals", true);
	leaf.onBeforeCompile = (shader): void => {
		shader.uniforms.uCustomNormals = {value: roundedNormals};
		shader.fragmentShader = `uniform bool uCustomNormals;\n` + shader.fragmentShader.replace(
			"#include <normal_fragment_begin>",
			THREE.ShaderChunk.normal_fragment_begin.replace("normal *= faceDirection;", "if (!uCustomNormals) { normal *= faceDirection; }"),
		);
	};

	return {bark, leaf};
}
